import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, ChevronDown } from 'lucide-react';

const YouTubeHeroSection = ({
  title,
  subtitle,
  ctaText = "Richiedi un preventivo personalizzato",
  ctaLink = "/preventivo",
  youtubeId,
  fallbackImageUrl,
  showScrollIndicator = true,
  overlay = 'light',
  minHeight = '100vh'
}) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Give time for YouTube to load
    const timer = setTimeout(() => setIsReady(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const overlayClass = overlay === 'light' 
    ? 'bg-black/40' 
    : 'bg-black/60';

  return (
    <section 
      className="relative w-full overflow-hidden"
      style={{ minHeight }}
    >
      {/* YouTube Background */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${window.location.origin}`}
          title="Background Video"
          className="absolute top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{ 
            border: 'none',
            minWidth: '100vw',
            minHeight: '100vh'
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>

      {/* Fallback Image (shown briefly while YouTube loads) */}
      {fallbackImageUrl && (
        <img
          src={fallbackImageUrl}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            isReady ? 'opacity-0' : 'opacity-100'
          }`}
        />
      )}

      {/* Overlay */}
      <div className={`absolute inset-0 ${overlayClass} z-10`} />

      {/* Content */}
      <div 
        className="relative z-20 flex flex-col items-center justify-center text-center px-6 lg:px-12"
        style={{ minHeight }}
      >
        <div className="max-w-4xl mx-auto animate-fade-in">
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light text-white leading-tight mb-6 tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg md:text-xl lg:text-2xl text-white/80 font-light mb-10 max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
          {ctaText && (
            <Link to={ctaLink}>
              <Button 
                className="bg-sky-500 hover:bg-sky-600 text-white px-8 md:px-12 py-4 md:py-6 text-base md:text-lg font-medium rounded-none transition-all duration-300 hover:scale-105 group"
              >
                {ctaText}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </Link>
          )}
        </div>

        {/* Scroll Indicator */}
        {showScrollIndicator && (
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-white/60" />
          </div>
        )}
      </div>
    </section>
  );
};

export default YouTubeHeroSection;
